import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, endlessPlayUrl, playUrlFor, previewUrlFor, type ActivityEntry, type CollectorRuns, type DecadeSpread, type ReadinessProblem, type PlaySession, type PreflightResult, type RoundDetail, type RoundItem, type RoundSummary, type ShareLink } from '../lib/api';
import { CSV_TEMPLATE, csvToItems, itemsToCsv } from '../lib/csv';
import { exactTime, timeAgo } from '../lib/timeAgo';
import ItemCard from './ItemCard';
import { HEADLINE_SIZES, HEADLINE_SIZE_LABELS, type HeadlineSize } from '../play/headlineScale';
import { NO_FILTERS, decadesPresent, filterItems, filtersActive, type ItemFilters } from './filterItems';
import { FILE_LIMIT, buildOfflineHtml, overSizeLimit, sizeMb } from './offlineExport';
import TourLink from '../tour/TourLink';

type Panel = 'none' | 'csv' | 'links' | 'activity' | 'sessions' | 'trash';

/* The Real Headlines bank: /endless
 *
 * The same editor, not a copy of it. Endless is a reserved round row, so every headline
 * behaviour — add, edit, upload, reorder, CSV, history — is identical and stays identical. A
 * forked editor would drift within a month, and the item editing is the bulk of this file. */
export function EndlessEditor() {
  return <RoundEditor variant="endless" />;
}

/* The Intake Queue page: /intake
 *
 * Same editor again. A candidate is edited exactly like a headline — because once it is
 * approved it IS one, moved rather than copied — so a separate screen would be the same screen
 * with a different word at the top, drifting apart a fix at a time. */
export function IntakeEditor() {
  return <RoundEditor variant="intake" />;
}

/* The Fake Headlines bank: /fake
 *
 * Split out of the Endless Queue 2026-08-21. Real headlines are FOUND — the collector and the
 * extension both produce only reals — while fake ones are WRITTEN and then need a graphic drawn.
 * In one bucket the artwork backlog was invisible: 29 reals that needed nothing sat alongside 8
 * fakes, every one of them waiting on art, and no count anywhere said so.
 *
 * Same editor for the fourth time, and for the strongest reason yet: this bank exists to make
 * `needs-art` visible, and the readiness panel that shows it already lives here. */
export function FakeEditor() {
  return <RoundEditor variant="fake" />;
}

interface Props {
  /* 'endless' (the REAL bank) hides the series lifecycle chrome and reveals the order toggle.
     'fake' is the same, for the half that needs artwork before it can be recorded.
     'intake' is a review queue: no lifecycle, no play link, and a Reject action. */
  variant?: 'series' | 'endless' | 'intake' | 'fake';
}

export default function RoundEditor({ variant = 'series' }: Props) {
  const { roundId: paramRoundId = '' } = useParams();
  const navigate = useNavigate();
  const endless = variant === 'endless';
  const intake = variant === 'intake';
  const fake = variant === 'fake';
  /* All three reserved pools are singletons with no id in the URL and no series lifecycle. Most
     of the chrome decisions are the same for each, so they share one flag and only differ where
     they genuinely differ. */
  const pool = endless || intake || fake;

  const [round, setRound] = useState<RoundDetail | null>(null);
  const [problems, setProblems] = useState<ReadinessProblem[]>([]);
  const [spread, setSpread] = useState<DecadeSpread | null>(null);
  const [collectors, setCollectors] = useState<CollectorRuns | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>('none');
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  /* Bulk move. A Set rather than a flag on each item so selection survives a reload of the
     list without having to be merged back into it. */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<ItemFilters>(NO_FILTERS);

  const load = useCallback(async () => {
    try {
      // The endless pool is a singleton with no id in the URL — the server resolves it (and
      // creates it on first use), so the page has a stable address that survives the row.
      const res = intake ? await api.intake()
        : fake ? await api.fake()
          : endless ? await api.endless()
            : await api.getRound(paramRoundId);
      setRound(res.round);
      setProblems(res.problems);
      setSpread(res.spread ?? null);
      setCollectors(collectorsOf(res));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that round.');
    }
  }, [endless, intake, fake, paramRoundId]);

  /* Known from the URL for a series, and only after loading for endless. Every use below runs
     from a handler, i.e. after the first render with a loaded round, so it is never empty in
     practice. */
  const roundId = pool ? round?.id ?? '' : paramRoundId;

  useEffect(() => {
    void load();
  }, [load]);

  // Switching between Series and a pool must not carry a selection — or a filter — across.
  useEffect(() => { setPicked(new Set()); setFilters(NO_FILTERS); }, [endless, intake, fake, paramRoundId]);

  /* Scroll a headline into view and mark it, from a readiness link.
   *
   * ⚠️ Clears the filter first when the target is hidden. Adding jump links to a filterable list
   * has one obvious failure — you click a number, the headline is filtered out, and nothing at
   * all happens — and it is invisible to anyone testing with no filter on. The scroll waits a
   * frame in that case so the row exists to scroll to.
   *
   * Reordering is already switched off while filtering, so clearing the filter here also puts
   * the arrows back; there is no third state to reason about. */
  const jumpToHeadline = useCallback((id: string) => {
    const go = () => {
      const el = document.getElementById(`headline-${id}`);
      if (!el) return;
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
      el.classList.add('jumped');
      window.setTimeout(() => el.classList.remove('jumped'), 1600);
    };
    if (document.getElementById(`headline-${id}`)) { go(); return; }
    setFilters(NO_FILTERS);
    // One frame for React to re-render the unfiltered list before looking for the row.
    requestAnimationFrame(() => requestAnimationFrame(go));
  }, []);

  const togglePick = (id: string, on: boolean) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  const replaceItem = (item: RoundItem) =>
    setRound((r) => (r ? { ...r, items: r.items.map((i) => (i.id === item.id ? item : i)) } : r));

  async function addItem(verdict: 'real' | 'fake') {
    try {
      const item = await api.createItem(roundId, { verdict, headline: '' });
      setRound((r) => (r ? { ...r, items: [...r.items, item] } : r));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add a headline.');
    }
  }

  async function deleteItem(id: string) {
    // Still asks, but no longer implies it is final — it is in Recently Deleted for 30 days.
    if (!confirm('Delete this headline?\n\nIt goes to Recently Deleted and can be restored for 30 days.')) return;
    try {
      await api.deleteItem(id);
      setRound((r) => (r ? { ...r, items: r.items.filter((i) => i.id !== id) } : r));
      togglePick(id, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that headline.');
    }
  }

  const moveItem = (id: string, direction: -1 | 1) => {
    if (!round) return;
    const from = round.items.findIndex((i) => i.id === id);
    if (from < 0) return;
    void moveItemTo(id, from + direction);
  };

  /* Insertion point in the CURRENT order -> final index once the dragged row has left the
     list. Dropping below row 3 means "insert before 4"; if the row came from above, everything
     shifts up by one as it leaves, so the final index is 3 rather than 4. Getting this wrong
     lands the row one place off, which is maddening precisely because it almost works. */
  function moveItemBefore(id: string, insertBefore: number) {
    if (!round) return;
    const from = round.items.findIndex((i) => i.id === id);
    if (from < 0) return;
    void moveItemTo(id, insertBefore > from ? insertBefore - 1 : insertBefore);
  }

  /* Move to an arbitrary position — used by the arrows and by dropping a dragged row. The
     server writes a single midpoint position; if the gap between two neighbours has collapsed
     it compacts the whole round and hands back the new order. */
  async function moveItemTo(id: string, to: number) {
    if (!round) return;
    const items = round.items;
    const from = items.findIndex((i) => i.id === id);
    if (from < 0 || to < 0 || to >= items.length || to === from) return;

    // Optimistic: reorder locally so the list does not jump while the request is in flight.
    const next = items.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRound({ ...round, items: next });
    setBusyItem(id);

    const after = next[to - 1]?.position;
    const before = next[to + 1]?.position;
    try {
      const res = await api.moveItem(roundId, id, after, before);
      if (res.compacted && res.items) setRound((r) => (r ? { ...r, items: res.items! } : r));
      else if (res.item) replaceItem(res.item);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reorder.');
      void load(); // put the real order back
    } finally {
      setBusyItem(null);
    }
  }

  async function renameRound(title: string) {
    if (!round || title.trim() === round.title) return;
    try {
      const updated = await api.updateRound(roundId, { title: title.trim() });
      setRound((r) => (r ? { ...r, title: updated.title } : r));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not rename.');
    }
  }

  async function deleteRound() {
    if (!round) return;
    if (!confirm(`Delete "${round.title}" and all ${round.items.length} of its headlines? This cannot be undone.`)) return;
    try {
      await api.deleteRound(roundId);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the round.');
    }
  }

  if (error && !round) {
    return (
      <div className="state-block">
        <div className="big">That series could not be opened</div>
        <div className="sub">{error}</div>
        <Link className="btn primary" to="/" style={{ textDecoration: 'none' }}>Back to Series</Link>
      </div>
    );
  }
  if (!round) return <div className="state-block"><div className="sub">Loading…</div></div>;

  /* Filtering hides rows; it never reorders or renumbers them. `indexOf` keeps each card's true
     position so the number on screen still names the same headline with a search running. */
  const filtering = filtersActive(filters);
  const visible = filterItems(round.items, filters);
  const indexOf = new Map(round.items.map((i, n) => [i.id, n]));

  const ready = problems.length === 0 && round.items.length > 0;
  /* Arrived at a reserved pool through /rounds/<id> rather than its own page.
   *
   * `variant` comes from the ROUTE, so this editor renders a bank as an ordinary series when
   * somebody has its id — which is how a Delete Series button ended up pointed at a bank holding
   * live headlines. The server refuses that delete now; this stops the button being offered in
   * the first place, because a button that exists to error is worse than no button.
   * `kind &&` because a row written before the field existed reads "" and is a real series. */
  const reservedRound = Boolean(round.kind && round.kind !== 'series');

  return (
    <>
      <div className="page-head">
        <div style={{ minWidth: 0 }}>
          {intake ? (
            <>
              <span className="crumb">Collected automatically</span>
              <h1 className="page-title">Intake Queue</h1>
              <p className="page-sub">
                {round.items.length === 0
                  ? 'Nothing waiting. New headlines appear here when the collector runs.'
                  : `${round.items.length} waiting for review · Adorian never sees these`}
                <CollectorStamp collectors={collectors} />
              </p>
              <DecadeLine spread={spread} filters={filters} onChange={setFilters} />
            </>
          ) : endless ? (
            <>
              <span className="crumb">Endless Mode</span>
              {/* Named for the nav tab and the move-destination dropdown, both of which the
                  2026-08-21 split renamed. It was still headed "Endless Queue" afterwards, so
                  the same bank had two names depending on where you read it. "Endless Mode"
                  stays as the crumb because that is what this feeds, not what it is. */}
              <h1 className="page-title">Real Headlines</h1>
              <p className="page-sub">
                {round.items.length} headline{round.items.length === 1 ? '' : 's'} · never appears in a series
              </p>
              {/* "Spanning roughly the last few decades" is a requirement of the endless pool
                  specifically, and a pool that has quietly become all-2020s looks exactly like a
                  healthy one until someone counts.
                  It is on the series page too since 2026-08-17. It was left off deliberately —
                  a series is a planned episode with no decade requirement — but once the counts
                  became filter buttons that reasoning stopped holding: "show me the undated ones"
                  is worth having wherever there are headlines to fix. */}
              <DecadeLine spread={spread} filters={filters} onChange={setFilters} />
            </>
          ) : fake ? (
            <>
              <span className="crumb">Written in-house</span>
              <h1 className="page-title">Fake Headlines</h1>
              <p className="page-sub">
                {round.items.length} headline{round.items.length === 1 ? '' : 's'}
                {/* The number this bank exists to make visible. Mixed in with the real ones it
                    was invisible: 29 reals that needed nothing, 8 fakes every one of them
                    waiting on art, and no count anywhere saying so. */}
                {(() => {
                  const waiting = problems.filter((p) => p.kind === 'needs-art').length;
                  return waiting > 0
                    ? <> · <span className="c-warn">{waiting} waiting on artwork</span></>
                    : <> · <span className="c-ok">all artwork done</span></>;
                })()}
              </p>
              <DecadeLine spread={spread} filters={filters} onChange={setFilters} />
            </>
          ) : (
            <>
              <Link to="/" className="crumb">← All Series</Link>
              <input
                className="round-title-input"
                defaultValue={round.title}
                aria-label="Series name"
                onBlur={(e) => renameRound(e.target.value)}
              />
              <p className="page-sub">
                {round.items.length} headline{round.items.length === 1 ? '' : 's'}
                {round.targetSize ? ` · target ${round.targetSize}` : ''}
                {' · '}
                {ready ? <span className="c-ok">ready to record</span> : <span className="c-warn">not ready yet</span>}
              </p>
              <DecadeLine spread={spread} filters={filters} onChange={setFilters} />
            </>
          )}
        </div>
        <div className="head-actions">
          <button className="btn" onClick={() => setPanel(panel === 'csv' ? 'none' : 'csv')}>Import / Export</button>
          {/* Not on intake. Nothing there is ever played — it is a review queue — so the panel
              would always be empty, and a button called "Plays" on the one screen full of
              unvetted headlines reads as an invitation to play them. */}
          {!intake && (
            <button className="btn" onClick={() => setPanel(panel === 'sessions' ? 'none' : 'sessions')}>Plays</button>
          )}
          <button className="btn" onClick={() => setPanel(panel === 'activity' ? 'none' : 'activity')}>History</button>
          <button className="btn" onClick={() => setPanel(panel === 'trash' ? 'none' : 'trash')}>Recently Deleted</button>
          {/* Straight into the full experience. Previously the only way to see your own round
              was to mint a share link, copy it, then open it. */}
          {/* Nothing in intake is playable — it is unreviewed, and offering a play button on a
              screen full of unvetted headlines invites exactly the mistake this queue exists to
              prevent. Individual candidates still have Open Live on their card. */}
          {/* ⚠️ Nothing to play on the fake bank, deliberately: a run of nothing but fabricated
              headlines is not the game, and a button offering it is one click away from putting
              it on camera. Its headlines reach him merged into Endless Mode, or in a series. */}
          {!intake && !fake && (
            <a
              className="btn primary"
              href={endless ? endlessPlayUrl() : previewUrlFor(roundId)}
              target="_blank"
              rel="noreferrer"
            >
              {endless ? 'Play Endless' : 'Review Series'}
            </a>
          )}
          {/* No share link for the pool: Endless is reached from Adorian's Modes screen, and a
              second, public way in would be one more thing to keep revoked. */}
          {!pool && (
            <button className="btn" onClick={() => setPanel(panel === 'links' ? 'none' : 'links')}>Share Link</button>
          )}
        </div>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

      {intake && <IntakeExplainer />}

      {/* Look settings decide how a page RENDERS on camera. A candidate is not going on camera
          until it has been moved somewhere, and it inherits that pool's look when it does. */}
      {!intake && (
      <RoundStyle
        round={round}
        endless={endless}
        fakeBank={fake}
        pool={pool}
        onChanged={(patch) => setRound((cur) => (cur ? { ...cur, ...patch } : cur))}
        onError={setError}
      />
      )}

      <TroubleSpots
        title="Not ready to record"
        groups={groupProblems(problems)}
        total={round.items.length}
        onJump={jumpToHeadline}
        onShowOnly={(ids) => setFilters({ ...NO_FILTERS, ids })}
      />

      <ShootReady roundId={roundId} items={round.items} onJump={jumpToHeadline} />

      {/* Series only. The pools are working lists, not something taken to a shoot as a file. */}
      {!pool && <OfflineExport roundId={roundId} title={round.title} count={round.items.length} />}

      <FilterBar
        filters={filters}
        onChange={setFilters}
        items={round.items}
        showing={visible.length}
      />

      <MoveBar
        picked={picked}
        /* The VISIBLE count, because Select All selects what is on screen. Offering "select all
           43" under a search showing 6 is how someone moves 37 headlines they never looked at. */
        total={visible.length}
        fromRoundId={roundId}
        fromEndless={endless}
        fromFake={fake}
        fromIntake={intake}
        onSelectAll={() => setPicked(new Set(visible.map((i) => i.id)))}
        onClear={() => setPicked(new Set())}
        onMoved={() => { setPicked(new Set()); void load(); }}
        onError={setError}
      />

      {panel === 'csv' && <CsvPanel roundId={roundId} items={round.items} onImported={load} />}
      {panel === 'links' && <LinksPanel roundId={roundId} ready={ready} />}
      {panel === 'sessions' && <SessionsPanel roundId={roundId} />}
      {panel === 'activity' && <ActivityPanel roundId={roundId} />}
      {panel === 'trash' && <TrashPanel roundId={roundId} onRestored={load} />}

      <div className="item-list">
        {visible.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            /* The item's TRUE position, never its position among the visible rows. The number on
               the card is the one people say out loud ("number 7 is dead"), and it has to keep
               meaning the same headline whether or not a filter is on. */
            index={indexOf.get(item.id) ?? 0}
            total={round.items.length}
            busy={busyItem === item.id}
            onChanged={replaceItem}
            onDeleted={deleteItem}
            /* Reordering is off while filtering. Both gestures are positional — ↓ swaps with the
               next headline, a drop inserts before this row — and with rows hidden the neighbour
               you get is not the neighbour you can see. Pressing ↓ would appear to do nothing
               while quietly moving the item past something off screen. */
            onMove={filtering ? undefined : moveItem}
            onDropBefore={filtering ? undefined : (dragId, insertBefore) => void moveItemBefore(dragId, insertBefore)}
            dragging={dragId}
            onDragState={setDragId}
            ribbonLabel={round.ribbonEnabled ? round.ribbonLabel : ''}
            headlineSize={round.headlineSize}
            showWordmark={round.wordmarkEnabled}
            roundId={roundId}
            selected={picked.has(item.id)}
            onSelect={togglePick}
          />
        ))}
        {filtering && visible.length === 0 && (
          <div className="state-block">
            <div className="sub">
              No headline here matches that. {round.items.length} in this list.
            </div>
          </div>
        )}
      </div>

      <div className="add-row">
        {/* Intake is filled by the collector, not by hand. A headline someone types belongs in a
            series or the pool directly — routing it through a review queue reviews it against
            nothing. */}
        {/* ⚠️ A bank only offers its OWN verdict. Offering "+ Add Real Headline" on the fake bank
            invites precisely the mis-filing the 2026-08-21 split exists to prevent, and a
            headline in the wrong bank is invisible in the only place anyone would look for it.
            A series offers both, because a series needs both. */}
        {!intake && !fake && <button className="btn primary" onClick={() => addItem('real')}>+ Add Real Headline</button>}
        {!intake && !endless && <button className="btn primary" onClick={() => addItem('fake')}>+ Add Fake Headline</button>}
        {/* The pool is a singleton the app recreates on demand, so "delete" would only empty it
            confusingly. Headlines are removed one at a time, like anywhere else. */}
        {!pool && !reservedRound && (
          <button className="btn ghost danger" onClick={deleteRound} style={{ marginLeft: 'auto' }}>Delete Series</button>
        )}
      </div>
    </>
  );
}

/* Download a copy that plays with no network.
 *
 * The standing commitment in CLAUDE.md, and cover for the app's biggest structural risk: every
 * graphic on a shoot arrives through one Cloudflare tunnel to one Mac Mini.
 *
 * Built in the browser rather than on the edge. A round of fake pages is several megabytes of
 * artwork once base64'd, which is a poor fit for a Pages Function's memory and time budget — and
 * doing it here means the progress is real rather than a spinner over a request that might be
 * about to time out. */
function OfflineExport({ roundId, title, count }: { roundId: string; title: string; count: number }) {
  const [state, setState] = useState<'idle' | 'working' | 'done'>('idle');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function download() {
    setState('working');
    setError(null);
    setNote('starting');
    try {
      /* The same payload the play surface gets, so what is exported is exactly what plays. */
      const round = await api.preview(roundId);
      const result = await buildOfflineHtml(round, title, (done, total, label) => {
        setNote(total > 1 ? `${label} ${done}/${total}` : label);
      });

      const url = URL.createObjectURL(new Blob([result.html], { type: 'text/html' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = result.filename;
      a.click();
      // Revoked on the next tick — revoking immediately can cancel the download in Safari.
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      setState('done');
      setNote([
        `${result.filename} · ${sizeMb(result.bytes)} MB`,
        result.missing > 0 ? ` · ${result.missing} image${result.missing === 1 ? '' : 's'} could not be included` : '',
        overSizeLimit(result.bytes) ? ` · over ${FILE_LIMIT} MB, check it opens before you rely on it` : '',
      ].join(''));
    } catch (err) {
      setState('idle');
      setError(err instanceof Error ? err.message : 'Could not build the offline copy.');
    }
  }

  return (
    <div className="offline-row">
      <TourLink to="offline" label="the offline copy for shoot day" />
      <button className="btn" disabled={state === 'working' || count === 0} onClick={() => void download()}>
        {state === 'working' ? `Building… ${note}` : 'Download offline copy'}
      </button>
      {state === 'done' ? (
        <span className="hint-sm">Saved {note}</span>
      ) : (
        <span className="hint-sm">
          One file that plays with no network — the app, the fonts and every fake page's artwork.
          A real headline's reveal still opens the live article.
        </span>
      )}
      {error && <span className="hint-sm bad">{error}</span>}
    </div>
  );
}

/* A set of headlines that need attention, grouped and linked.
 *
 * ── Why this exists ──
 * The readiness banner rendered one line per problem. On the Endless Queue that was twelve
 * near-identical sentences — "Headline 5 is FAKE and is waiting on artwork", then 9, then 11 —
 * about 340px of one thing said twelve times, with no way to collapse it and no way to act on
 * it. You read "Headline 39" and then scrolled 56 headlines hunting for card 39.
 *
 * Grouping by KIND is what reclaims the space: twelve lines become one row with twelve numbers.
 * The numbers are the point — each one jumps to that headline.
 *
 * Used by BOTH the readiness banner and the Shoot-Ready results, because they are the same
 * thing: a list of headlines with something wrong. Two components would have drifted, and two
 * differently-shaped problem lists on one page is worse than either.
 */
interface TroubleGroup {
  /* What is wrong, in the plural — "Waiting on artwork". THE GROUP TITLE IS THE REASON, which
     is why the readiness banner groups by kind and the link check groups by verdict: a broken
     link's reason is different for every headline, so lumping them under one title would hide
     the only useful thing the check produced. */
  title: string;
  /** The headlines it applies to, in list order. */
  spots: {
    id: string;
    index: number;
    /* Shown on the button instead of the number. A readiness problem needs no label — the row
       already says what is wrong, so the position is the whole identity. A broken link does:
       "which headline" is the question, and a number sends you counting. */
    label?: string;
    message?: string;
  }[];
}

function TroubleSpots({
  title, groups, total, tone = 'bad', onJump, onShowOnly,
}: {
  /** The always-visible summary line, e.g. "Not ready to record". */
  title: string;
  groups: TroubleGroup[];
  /** Denominator for the summary — how many headlines there are in all. */
  total: number;
  tone?: 'bad' | 'warn';
  onJump: (id: string) => void;
  onShowOnly?: (ids: string[]) => void;
}) {
  /* Expanded by default. Grouping already shrinks this to a couple of lines, so nothing needs
     hiding — and a problem nobody can see is a problem nobody fixes. The toggle is for when it
     is in the way, which was the actual complaint. */
  const [open, setOpen] = useState(true);
  const count = groups.reduce((n, g) => n + g.spots.length, 0);
  if (!count) return null;

  return (
    <div className={`trouble ${tone}`}>
      <div className="trouble-head">
        <strong>{title}</strong>
        <TourLink to="readiness" label="the not-ready list, and jumping to a headline" />
        <span className="trouble-count">
          {count} of {total} headline{total === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          className="btn ghost trouble-toggle"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      {open && groups.filter((g) => g.spots.length).map((group) => (
        <div className="trouble-group" key={group.title}>
          <span className="trouble-kind">{group.title} · {group.spots.length}</span>
          <span className="trouble-jumps">
            {group.spots.map((spot) => (
              <button
                type="button"
                key={spot.id}
                className={`jump${spot.label ? ' wide' : ''}`}
                /* The sentence the server wrote, kept as the tooltip — grouping shows what is
                   wrong, and this still says it in full for the one headline. */
                title={spot.message || `Jump to headline ${spot.index + 1}`}
                onClick={() => onJump(spot.id)}
              >
                {spot.label || spot.index + 1}
              </button>
            ))}
          </span>
          {onShowOnly && group.spots.length > 1 && (
            <button
              type="button"
              className="trouble-only"
              onClick={() => onShowOnly(group.spots.map((s) => s.id))}
            >
              Show only these
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/* Link-check results, grouped by the REASON they failed.
 *
 * Not by "broken" — a 404, a redirect to the homepage and a page that now shows a different
 * article are three different jobs, and the verdict is the only thing the check actually
 * learned. Lumping them under one heading hides it, which is what the first version of this
 * did: it put the reason in a tooltip and the assertion "the reason is given, not just a
 * count" failed, correctly.
 *
 * Labelled with the headline rather than its number, because "which one" is the question here
 * and a number sends you counting rows. */
function byVerdict(
  rows: PreflightResult[],
  items: RoundItem[],
  byId: Map<string, RoundItem>,
): TroubleGroup[] {
  const groups = new Map<string, TroubleGroup>();
  for (const r of rows) {
    const title = r.verdict || 'Broken';
    if (!groups.has(title)) groups.set(title, { title, spots: [] });
    groups.get(title)!.spots.push({
      id: r.id,
      index: items.findIndex((i) => i.id === r.id),
      label: byId.get(r.id)?.headline?.slice(0, 60) || r.id,
      message: `${byId.get(r.id)?.headline || r.id} — ${r.verdict}`,
    });
  }
  return [...groups.values()];
}

/** Plural titles for each readiness problem, in the order they are worth fixing. */
const PROBLEM_TITLES: { kind: string; title: string }[] = [
  { kind: 'no-text', title: 'No headline text' },
  { kind: 'no-link', title: 'Missing a reveal link' },
  { kind: 'needs-art', title: 'Waiting on artwork' },
  { kind: 'placeholder-art', title: 'Still a placeholder' },
  { kind: 'empty-fake', title: 'Fake page is empty' },
];

/** Group readiness problems by kind, dropping the kinds that have none. */
function groupProblems(problems: ReadinessProblem[]): TroubleGroup[] {
  return PROBLEM_TITLES.map(({ kind, title }) => ({
    title,
    spots: problems.filter((p) => p.kind === kind),
  })).filter((g) => g.spots.length);
}

/* Search and filter over the list.
 *
 * Client-side over the already-loaded round, because the whole round is already in memory and a
 * round is hundreds of items at most. A server round-trip per keystroke would be slower and would
 * add a query surface for no gain.
 *
 * The decade options are built from what is actually present — an empty decade in the list is a
 * dead end someone has to try before learning it is empty.
 */
function FilterBar({
  filters, onChange, items, showing,
}: {
  filters: ItemFilters;
  onChange: (f: ItemFilters) => void;
  items: RoundItem[];
  showing: number;
}) {
  const set = (patch: Partial<ItemFilters>) => onChange({ ...filters, ...patch });
  const active = filtersActive(filters);
  const decades = decadesPresent(items);

  // Below this there is nothing to find; the bar would be chrome around a list you can already see.
  if (items.length < 5) return null;

  return (
    <div className="filter-bar">
      <TourLink to="filters" label="searching and filtering the list" />
      <input
        type="search"
        className="filter-search"
        placeholder="Search headlines, outlets and links"
        value={filters.q}
        onChange={(e) => set({ q: e.target.value })}
        aria-label="Search headlines"
      />
      <select value={filters.verdict} onChange={(e) => set({ verdict: e.target.value as ItemFilters['verdict'] })} aria-label="Real or fake">
        <option value="">Real and fake</option>
        <option value="real">Real only</option>
        <option value="fake">Fake only</option>
      </select>
      <select value={filters.flag} onChange={(e) => set({ flag: e.target.value as ItemFilters['flag'] })} aria-label="Show only">
        <option value="">Anything</option>
        <option value="needs-art">Waiting on artwork</option>
        <option value="no-link">Missing a link</option>
        <option value="undated">No published date</option>
      </select>
      {decades.length > 1 && (
        <select value={filters.decade} onChange={(e) => set({ decade: e.target.value })} aria-label="Decade">
          <option value="">Any decade</option>
          {decades.map((d) => <option key={d} value={d}>{d}s</option>)}
        </select>
      )}
      {active && (
        <>
          <span className="filter-count">
            Showing {showing} of {items.length}
            {/* Said plainly, because the arrows and drag handles visibly stop working. */}
            {showing > 0 && ' · reordering is off while filtering'}
          </span>
          <button className="btn ghost" onClick={() => onChange(NO_FILTERS)}>Clear</button>
        </>
      )}
    </div>
  );
}

/* The Shoot-Ready check.
 *
 * `readinessProblems` can only tell you a link EXISTS. This opens every one and confirms it
 * still lands on the article rather than a 404, a homepage, a bot wall or a different story —
 * the thing that actually ruins a take. Until now it was a terminal command, which meant one
 * person could run it and the two people who build the series could not check their own work.
 *
 * Walks the round a batch at a time because a Pages Function has a per-request budget; a
 * preflight that dies half-done would look like a pass.
 */
function ShootReady({ roundId, items, onJump }: { roundId: string; items: RoundItem[]; onJump: (id: string) => void }) {
  const [state, setState] = useState<'idle' | 'running' | 'done'>('idle');
  const [checked, setChecked] = useState(0);
  const [results, setResults] = useState<PreflightResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const linked = items.filter((i) => i.verdict === 'real' && i.sourceUrl.trim());
  const byId = new Map(items.map((i) => [i.id, i]));

  async function run() {
    setState('running');
    setResults([]);
    setChecked(0);
    setError(null);
    try {
      let ids: string[] | undefined;
      const all: PreflightResult[] = [];
      // Guard against a server that never empties `remaining` — a bounded loop, not while(true).
      for (let pass = 0; pass < 200; pass += 1) {
        const res = await api.preflight(roundId, ids);
        all.push(...res.results);
        setResults([...all]);
        setChecked(all.length);
        if (!res.remaining.length) break;
        ids = res.remaining;
      }
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The check could not finish.');
      setState('idle');
    }
  }

  if (!linked.length) return null;

  const broken = results.filter((r) => r.verdict);
  /* A block is separated from a break on purpose. This runs from Cloudflare's network, and some
     outlets treat a datacenter IP differently from home broadband — so "blocked" is real
     evidence, not proof, and the honest instruction is to spot-check it rather than to bin the
     headline. A 404 or a homepage redirect is the same from any address. */
  const blocked = broken.filter((r) => /^(blocked|rate-limited|a bot challenge)/.test(r.verdict));
  const dead = broken.filter((r) => !blocked.includes(r));

  return (
    <div className="preflight" data-testid="shoot-ready">
      <div className="preflight-head">
        <button className="btn" onClick={() => void run()} disabled={state === 'running'} data-testid="shoot-ready-run">
          {state === 'running' ? `Checking ${checked}/${linked.length}…` : 'Check every link'}
        </button>
        <TourLink to="shoot-ready" label="the pre-shoot link check" />
        <span className="hint-sm">
          {state === 'done'
            ? `${linked.length - broken.length} of ${linked.length} open the article.`
            : `Opens all ${linked.length} reveal links and confirms each still lands on its story. Worth running before a shoot — links rot on their own.`}
        </span>
      </div>

      {error && <div className="item-warn hard">{error}</div>}

      {/* The same grouped, jumpable list the readiness banner uses. These are the same thing —
          headlines with something wrong — and two differently-shaped problem lists on one page
          is worse than either. Grouped by VERDICT here rather than by kind, because that is what
          the link check reports and every one of them is a different sentence. */}
      {state === 'done' && dead.length > 0 && (
        <div data-testid="preflight-dead">
          <TroubleSpots
            title="Links that will not open the article"
            groups={byVerdict(dead, items, byId)}
            total={items.length}
            onJump={onJump}
          />
        </div>
      )}

      {state === 'done' && blocked.length > 0 && (
        <div data-testid="preflight-blocked">
          <TroubleSpots
            title="Outlets that refused this check"
            groups={byVerdict(blocked, items, byId)}
            total={items.length}
            tone="warn"
            onJump={onJump}
          />
          <span className="hint-sm">
            This check runs from a datacenter, which some outlets block even when the article
            opens fine from your laptop. Use <strong>Open ↗</strong> on the headline to see for
            yourself before dropping it.
          </span>
        </div>
      )}

      {state === 'done' && !broken.length && (
        <div className="preflight-ok" data-testid="preflight-clear">Every link opens its article.</div>
      )}
    </div>
  );
}

/* What this screen is, for whoever opens it first.
 *
 * Intake is the only screen in the app whose contents arrive without anyone doing anything, so
 * it is the only one where "what am I looking at and what do I do with it" is a real question.
 * Three steps, in the order they are actually done, because the middle one is the whole job.
 *
 * ⚠️ COLLAPSIBLE SINCE 2026-09-01 (Lucky), reversing "always visible rather than dismissible".
 * The original reason is still true and is why this is a COLLAPSE and not a DISMISS: people open
 * this screen about once a week, and a hint you closed in March is no help in June. So the
 * heading and the button never go away — collapsed, it still says "How this works" and is one
 * click from being back. What is remembered is the preference, not the dismissal.
 *
 * Remembered in localStorage, deliberately. It is a per-browser reading preference with no
 * consequence if it is lost, which is the one case the PB-over-localStorage rule names as fine.
 * Move it to a real preference row only if it should follow someone between machines.
 */
const EXPLAINER_HIDDEN_KEY = 'rof:intake-explainer-hidden';

/* Both wrapped: Safari in private mode THROWS on localStorage rather than returning null, and an
   uncaught throw here would take the whole queue down over a help panel. Unreadable means open,
   which is the default a first-time reader should get anyway. */
function readExplainerHidden(): boolean {
  try { return window.localStorage.getItem(EXPLAINER_HIDDEN_KEY) === '1'; } catch { return false; }
}
function writeExplainerHidden(hidden: boolean): void {
  try { window.localStorage.setItem(EXPLAINER_HIDDEN_KEY, hidden ? '1' : '0'); } catch { /* private mode — this session only */ }
}

function IntakeExplainer() {
  // Lazy initialiser: read the stored preference once, on mount, not on every render.
  const [open, setOpen] = useState<boolean>(() => !readExplainerHidden());

  const toggle = () => {
    setOpen((wasOpen) => {
      const next = !wasOpen;
      writeExplainerHidden(!next);
      return next;
    });
  };

  return (
    <div className={`explainer${open ? '' : ' is-collapsed'}`} data-testid="intake-explainer">
      <div className="explainer-head">
        <TourLink to="intake" label="reviewing intake — keeping, approving and removing" />
        <button
          type="button"
          className="btn ghost explainer-toggle"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="intake-explainer-body"
          data-testid="intake-explainer-toggle"
        >
          {open ? 'Hide' : 'How this works'}
        </button>
      </div>
      {open && (
      <div id="intake-explainer-body">
      <p className="explainer-lead">
        These arrived on their own. Collectors run every morning and add any headline they find
        that is readable and not already here, and every one is fact-checked against live sources
        before it lands. <strong>Nobody has read them yet</strong>, and Adorian never sees this
        screen.
      </p>
      <dl className="explainer-steps">
        <dt>Check it</dt>
        <dd><strong>Open ↗</strong> next to the link opens the article in a new tab. If it does not
          open, or it is not the story the headline promises, it is no use — the link is what
          proves the headline on camera.</dd>
        <dt>Keep it</dt>
        <dd>Tick it, then <strong>Move to</strong> <strong>Real Headlines</strong> or any series
          — the collector only ever finds real ones. It leaves this screen and becomes an ordinary
          headline you can edit.</dd>
        <dt>Bin it</dt>
        <dd>Tick it, then <strong>Reject</strong>. That is not just a delete — the link is
          remembered, so the collector will never offer it again.</dd>
      </dl>
      </div>
      )}
    </div>
  );
}

/* How far back the pool actually reaches.
 *
 * Typographic, deliberately: counts and a separator, no chips and no coloured bands. The point
 * is to be readable at a glance while stocking, not to be a dashboard.
 */
/* Only /intake carries collector runs — the other pools are filled by hand, and their responses
   have no such key. Pulled out rather than cast inline: the four load paths share one `res`, and
   TypeScript will not narrow that union on `in` alone. */
function collectorsOf(res: unknown): CollectorRuns | null {
  if (!res || typeof res !== 'object' || !('collectors' in res)) return null;
  return (res as { collectors: CollectorRuns | null }).collectors ?? null;
}

/* "Last pulled 3 hours ago" — the answer to "is this list current?".
 *
 * ⚠️ This is when a collector last RAN, not when a candidate last ARRIVED, and the difference is
 * the entire reason the line is worth having. A run that finds nothing new is a completely normal
 * outcome — r/offbeat has quiet days and the Wikipedia archive is nearly exhausted — so a stamp
 * taken from the newest item would show a date days old on a perfectly healthy pipeline, and
 * would look exactly the same as a collector that died. The server reads the job ledger, which
 * records every run either way.
 *
 * The tooltip carries each collector separately, so a weekly one that has quietly stopped is
 * still findable rather than masked by the daily one that is fine. */
function CollectorStamp({ collectors }: { collectors: CollectorRuns | null }) {
  // No ledger (unreadable) or no run ever: say nothing rather than guess.
  if (!collectors?.lastPulledAt) return null;
  const when = timeAgo(collectors.lastPulledAt);
  if (!when) return null;

  const detail = collectors.runs
    .map(({ label, cadence, at, ok }) => {
      if (!at) return `${label} (${cadence}) — has never run`;
      return `${label} (${cadence}) — ${exactTime(at)}${ok === false ? ' · FAILED' : ''}`;
    })
    .join('\n');

  const failed = collectors.ok === false;
  return (
    <span className={`collector-stamp${failed ? ' failed' : ''}`} title={detail} data-testid="collector-stamp">
      {' · '}
      {failed ? `Last pull failed ${when}` : `Last pulled ${when}`}
    </span>
  );
}

/* The decade spread, and the fastest way to filter by one.
 *
 * These numbers were the answer to "is the pool still spanning a few decades" and nothing more —
 * you read them, then went to the filter bar and picked the same decade out of a dropdown. The
 * count IS the filter, so it may as well be the button. Undated too, which is the one people
 * actually need: it is the worklist of headlines whose date nobody has filled in.
 *
 * Counts always describe the WHOLE round, never the filtered view — they come from the server
 * with the round. A count that changed when you clicked it would stop being an index of the pool
 * and start being a readout of your own filter, which is useless. */
function DecadeLine({
  spread, filters, onChange,
}: {
  spread: DecadeSpread | null;
  filters: ItemFilters;
  onChange: (f: ItemFilters) => void;
}) {
  if (!spread || (spread.dated === 0 && spread.undated === 0)) return null;

  if (spread.dated === 0) {
    return (
      <p className="page-sub decades">
        No publication dates recorded yet — paste a link on a headline and the date fills itself in.
      </p>
    );
  }

  /* Clicking the one already on clears it. There is no "All" button because that IS the all
     button — a filter you turn on with a click and off with the same click needs no third state. */
  const pickDecade = (decade: number) => {
    const value = String(decade);
    onChange(filters.decade === value
      ? { ...filters, decade: '' }
      : { ...filters, decade: value, flag: filters.flag === 'undated' ? '' : filters.flag });
  };
  const pickUndated = () => {
    onChange(filters.flag === 'undated'
      ? { ...filters, flag: '' }
      : { ...filters, flag: 'undated', decade: '' });
  };

  return (
    <p className="page-sub decades">
      <TourLink to="decades" label="filtering by decade, and finding undated headlines" />
      {spread.decades.map(({ decade, count }) => {
        const on = filters.decade === String(decade);
        return (
          <button
            key={decade}
            type="button"
            className={`decade-pick${on ? ' on' : ''}`}
            aria-pressed={on}
            onClick={() => pickDecade(decade)}
          >
            {decade}s <strong>{count}</strong>
          </button>
        );
      })}
      {spread.undated > 0 && (
        <button
          type="button"
          className={`decade-pick undated${filters.flag === 'undated' ? ' on' : ''}`}
          aria-pressed={filters.flag === 'undated'}
          onClick={pickUndated}
        >
          undated <strong>{spread.undated}</strong>
        </button>
      )}
    </p>
  );
}

/* Bulk move: "grab me ten out of Endless".
 *
 * Appears only once something is ticked, so a page nobody is moving anything on looks exactly
 * as it did before. Destinations are every other series plus Endless itself — the reverse
 * direction has to exist, because the undo for grabbing the wrong ten is otherwise a retype.
 *
 * A MOVE, not a copy. What leaves Endless is gone from Endless, which is the point and also
 * what keeps the two pools genuinely separate.
 */
function MoveBar({
  picked, total, fromRoundId, fromEndless, fromFake, fromIntake, onSelectAll, onClear, onMoved, onError,
}: {
  picked: Set<string>;
  total: number;
  fromRoundId: string;
  fromEndless: boolean;
  fromFake: boolean;
  /** Intake only: adds Reject, which removes the candidates AND remembers them. */
  fromIntake: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onMoved: (message: string) => void;
  onError: (m: string) => void;
}) {
  const [destinations, setDestinations] = useState<RoundSummary[] | null>(null);
  const [endlessId, setEndlessId] = useState('');
  const [fakeId, setFakeId] = useState('');
  const [dest, setDest] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  /* Loaded once the first headline is ticked, not on page load: most visits never move
     anything, and this is two extra requests. */
  const count = picked.size;
  useEffect(() => {
    if (!count || destinations) return;
    void (async () => {
      try {
        /* Neither bank is in listRounds by design — SERIES_FILTER excludes every reserved kind —
           so each has to be fetched to be offered. Skipped for the bank you are already standing
           in, which would otherwise offer "move these here". */
        const [series, real, fakeBank] = await Promise.all([
          api.listRounds(),
          fromEndless ? Promise.resolve(null) : api.endless().then((r) => r.round),
          fromFake ? Promise.resolve(null) : api.fake().then((r) => r.round),
        ]);
        setDestinations(series.filter((r) => r.id !== fromRoundId));
        setEndlessId(real?.id || '');
        setFakeId(fakeBank?.id || '');
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Could not load the list of series.');
      }
    })();
  }, [count, destinations, fromEndless, fromFake, fromRoundId, onError]);

  if (!count) return null;

  const label = `${count} headline${count === 1 ? '' : 's'}`;

  async function move() {
    setBusy(true);
    setDone(null);
    try {
      const to = dest === '__new'
        ? { newSeriesTitle: newTitle.trim() || 'Untitled Series' }
        : { toRoundId: dest };
      const res = await api.moveItems([...picked], to);
      /* Reports what ACTUALLY moved. The batch is not atomic, so claiming the number asked for
         would be a lie exactly when it matters. */
      const moved = `${res.movedCount} headline${res.movedCount === 1 ? '' : 's'}`;
      setDone(
        res.failedCount
          ? `Moved ${moved} to "${res.round.title}". ${res.failedCount} could not be moved.`
          : `Moved ${moved} to "${res.round.title}".`,
      );
      setNewTitle('');
      setDest('');
      onMoved(`Moved ${moved}`);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not move those headlines.');
    } finally {
      setBusy(false);
    }
  }

  /* Reject is intake-only and it is not a delete.
     The URL goes onto the reject list, so the collector never offers the article again. Without
     that the same candidates return on the next run and the queue becomes something nobody
     trusts — which is the whole reason a plain delete is not enough here. */
  async function reject() {
    if (!confirm(`Reject ${label}? They will be removed from the queue and never collected again.`)) return;
    setBusy(true);
    setDone(null);
    try {
      const res = await api.rejectIntake([...picked]);
      setDone(
        res.failedCount
          ? `Rejected ${res.rejectedCount}. ${res.failedCount} could not be rejected.`
          : `Rejected ${res.rejectedCount} — they will not be collected again.`,
      );
      onMoved(`Rejected ${res.rejectedCount}`);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not reject those.');
    } finally {
      setBusy(false);
    }
  }

  const ready = dest === '__new' ? newTitle.trim().length > 0 : dest.length > 0;

  return (
    <div className="move-bar">
      <span className="move-count">{label} selected</span>
      <button className="btn ghost" onClick={onSelectAll} disabled={count === total}>Select All</button>
      <button className="btn ghost" onClick={onClear}>Clear</button>
      {fromIntake && (
        <button className="btn ghost danger" onClick={() => void reject()} disabled={busy} data-testid="reject-btn">
          {busy ? 'Working…' : `Reject ${label}`}
        </button>
      )}

      {/* Grouped so the destination controls wrap as one unit rather than the label, the select
          and the button each breaking onto their own line. */}
      <div className="move-actions">
        <label className="move-dest">
          <span className="lab">Move to</span>
          <select value={dest} disabled={busy} onChange={(e) => setDest(e.target.value)}>
            <option value="">Choose a destination…</option>
            <option value="__new">+ New Series…</option>
            {/* Both banks, so a headline filed in the wrong one is one move from the right one —
                which is the whole point of splitting them. */}
            {endlessId && <option value={endlessId}>Real Headlines</option>}
            {fakeId && <option value={fakeId}>Fake Headlines</option>}
            {(destinations || []).map((r) => (
              <option key={r.id} value={r.id}>{r.title}</option>
            ))}
          </select>
        </label>

        {dest === '__new' && (
          <input
            type="text"
            className="move-new"
            value={newTitle}
            placeholder="Name the new series"
            aria-label="New series name"
            disabled={busy}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && ready) { e.preventDefault(); void move(); } }}
          />
        )}

        <button className="btn primary" onClick={() => void move()} disabled={busy || !ready}>
          {busy ? 'Moving…' : `Move ${label}`}
        </button>
      </div>

      {done && <span className="move-done">{done}</span>}
    </div>
  );
}

/* Everything that decides how a round LOOKS, in one bar, because these are properties of the
   round rather than of any headline. The banner used to be per-item, which meant turning it off
   was ten edits and nothing stopped two pages disagreeing. */
function RoundStyle({
  round, endless, fakeBank, pool, onChanged, onError,
}: {
  round: RoundDetail;
  endless: boolean;
  /** The fake bank, which owns the banner shown on its own pages in Endless Mode. */
  fakeBank: boolean;
  /** Any reserved bank. Pools have no series lifecycle, so they show no Status control. */
  pool: boolean;
  onChanged: (patch: Partial<RoundDetail>) => void;
  onError: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function save(next: Parameters<typeof api.updateRound>[1]) {
    setBusy(true);
    try {
      const updated = await api.updateRound(round.id, next);
      onChanged({
        status: updated.status,
        ribbonEnabled: updated.ribbonEnabled,
        ribbonLabel: updated.ribbonLabel,
        headlineSize: updated.headlineSize,
        wordmarkEnabled: updated.wordmarkEnabled,
        playOrder: updated.playOrder,
      });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="round-settings">
      {/* Status is the hand-over switch for a series — it decides what shows on Adorian's picker.
          A bank is always available, so it has no lifecycle to set.
       *
       * ⚠️ THE ORDER TOGGLE IS GONE, from both banks, since the 2026-08-21 split.
       * Endless Mode merges the two banks and ALWAYS shuffles, because playing two concatenated
       * banks in their own order puts every real headline first — 29 then 8 — and the POSITION
       * gives the verdict away before he has read the words. With two banks there is also no
       * single hand-arranged order left to express. Leaving the toggle up would have been a
       * control that silently does nothing, which is worse than no control; arranging an order
       * now happens on a series, which is where curation lives. */}
      {pool ? null : (
        <label className="field setting">
          <span className="lab">Status</span>
          <select
            value={round.status}
            disabled={busy}
            onChange={(e) => save({ status: e.target.value as RoundDetail['status'] })}
          >
            <option value="draft">Draft</option>
            <option value="ready">Ready to Record</option>
            <option value="recorded">Recorded</option>
            <option value="archived">Archived</option>
          </select>
        </label>
      )}

      <div className="field setting">
        <span className="lab">Headline size</span>
        <div className="segmented">
          {HEADLINE_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className={round.headlineSize === size ? 'on' : ''}
              disabled={busy}
              onClick={() => save({ headlineSize: size as HeadlineSize })}
            >
              {HEADLINE_SIZE_LABELS[size]}
            </button>
          ))}
        </div>
      </div>

      <div className="field setting grow">
        <span className="lab">Top left banner</span>
        <div className="setting-row">
          <label className="switch">
            <input
              type="checkbox"
              checked={round.ribbonEnabled}
              disabled={busy}
              onChange={(e) => save({ ribbonEnabled: e.target.checked })}
            />
            <span>Show</span>
          </label>
          <input
            type="text"
            className="ribbon-input"
            defaultValue={round.ribbonLabel}
            placeholder="FABRICATED"
            aria-label="Top left banner text"
            disabled={!round.ribbonEnabled || busy}
            onBlur={(e) => { if (e.target.value !== round.ribbonLabel) save({ ribbonLabel: e.target.value }); }}
          />
          <label className="switch">
            <input
              type="checkbox"
              checked={round.wordmarkEnabled}
              disabled={busy}
              onChange={(e) => save({ wordmarkEnabled: e.target.checked })}
            />
            <span>Top Right Logo</span>
          </label>
        </div>
      </div>

      <span className="hint-sm">
        {/* Long headlines shrink on their own, so this is a nudge rather than a fixed size. */}
        Applies to every page in {pool ? 'this bank' : 'this series'}. Long headlines shrink
        automatically to stay clear of the banner and wordmark.
        {/* Said here because the Order toggle it replaced is gone: Endless Mode merges the two
            banks and always shuffles, so there is no order to set on a bank any more. */}
        {endless && ' Endless Mode shuffles the two banks together and keeps that order for the session.'}
        {fakeBank && ' The banner shows on these pages in Endless Mode, and this is where it is set.'}
      </span>
    </div>
  );
}

// ── panels ──────────────────────────────────────────────────────────────────────────────────

function LinksPanel({ roundId, ready }: { roundId: string; ready: boolean }) {
  const [links, setLinks] = useState<ShareLink[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => setLinks(await api.listLinks(roundId).catch(() => [])), [roundId]);
  useEffect(() => { void load(); }, [load]);

  async function mint() {
    setBusy(true);
    try {
      const link = await api.createLink(roundId);
      setLinks((l) => [link, ...(l || [])]);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    if (!confirm('Revoke this link? Anyone using it will lose access immediately.')) return;
    const updated = await api.revokeLink(id);
    setLinks((l) => (l || []).map((x) => (x.id === updated.id ? updated : x)));
  }

  async function copy(token: string) {
    await navigator.clipboard.writeText(playUrlFor(token)).catch(() => {});
    setCopied(token);
    setTimeout(() => setCopied(null), 2500);
  }

  const active = (links || []).filter((l) => !l.revoked);

  return (
    <div className="panel">
      <h2>Play links</h2>
      <p>
        Send one of these to the channel. It opens straight into the series — no sign-in, nothing
        to install — so it works on any recording machine. Links never expire; revoke one when
        you want it dead.
      </p>
      {!ready && (
        <div className="notice bad" style={{ marginBottom: 14 }}>
          This series is not ready to record yet. You can still make a link, but fix the problems
          above before sending it.
        </div>
      )}
      <div className="toolbar">
        <button className="btn primary" onClick={mint} disabled={busy}>{busy ? 'Creating…' : 'Create a New Link'}</button>
      </div>
      {links === null ? (
        <div className="hint-sm">Loading…</div>
      ) : active.length === 0 ? (
        <div className="hint-sm">No active links yet.</div>
      ) : (
        <div className="link-list">
          {active.map((l) => (
            <div className="link-row" key={l.id}>
              <code className="link-url">{playUrlFor(l.token)}</code>
              <span className="link-meta">{l.viewCount} view{l.viewCount === 1 ? '' : 's'}</span>
              <button className="btn" onClick={() => copy(l.token)}>{copied === l.token ? 'Copied' : 'Copy'}</button>
              <a className="btn ghost" href={playUrlFor(l.token)} target="_blank" rel="noreferrer">Open</a>
              <button className="btn ghost danger" onClick={() => revoke(l.id)}>Revoke</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CsvPanel({ roundId, items, onImported }: { roundId: string; items: RoundItem[]; onImported: () => void }) {
  const [text, setText] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [rejected, setRejected] = useState<{ row: number; reason: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  async function importCsv(replace: boolean) {
    /* The one destructive button in the app that had no confirm, and the most destructive one
       there is. It says exactly what is lost, because "are you sure?" would not have stopped
       anyone: the surprise was never the delete, it was that CSV carries no images, so replacing
       a round silently threw away artwork that takes a designer a day to redraw. */
    if (replace) {
      const fakes = items.filter((i) => i.verdict === 'fake' && i.images.length > 0).length;
      const warning = [
        `Replace all ${items.length} headline${items.length === 1 ? '' : 's'} in this series?`,
        fakes > 0
          ? `\n${fakes} fake page${fakes === 1 ? ' has' : 's have'} artwork. A CSV cannot carry images, so `
            + `${fakes === 1 ? 'it' : 'they'} will not come back with the import.`
          : '',
        '\nThey go to the trash and can be restored for 30 days.',
      ].join('');
      if (!confirm(warning)) return;
    }
    setBusy(true);
    setStatus(null);
    setRejected([]);
    try {
      const parsed = csvToItems(text);
      if (!parsed.items.length) throw new Error('No usable rows found.');
      const res = await api.bulkCreate(roundId, parsed.items, replace);
      setRejected([...parsed.rejected, ...res.rejected]);
      setStatus(`Imported ${res.created.length} headline${res.created.length === 1 ? '' : 's'}.`);
      setText('');
      onImported();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  function download() {
    const blob = new Blob([itemsToCsv(items)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'series.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 0);
  }

  return (
    <div className="panel">
      <h2>Import / Export</h2>
      <p>
        Columns: <code>verdict, headline, source_name, source_url, image_hint</code>. Only
        verdict and headline are required, and the header spelling is forgiving. A spreadsheet
        cannot carry images — put the filename in <code>image_hint</code> and upload the images
        on each fake page afterwards.
      </p>
      <div className="toolbar">
        <button className="btn" onClick={() => fileInput.current?.click()}>Choose a .CSV</button>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(e) => {
          const f = e.target.files?.[0];
          if (!f) return;
          const reader = new FileReader();
          reader.onload = () => setText(String(reader.result || ''));
          reader.readAsText(f);
          e.target.value = '';
        }} />
        <button className="btn primary" onClick={() => importCsv(false)} disabled={busy || !text.trim()}>Add to This Series</button>
        <button className="btn danger" onClick={() => importCsv(true)} disabled={busy || !text.trim()}>Replace Everything</button>
        <button className="btn ghost" onClick={() => setText(CSV_TEMPLATE)}>Paste a Template</button>
        <button className="btn ghost" onClick={download} disabled={!items.length}>Export Current Series</button>
      </div>
      <textarea rows={7} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste CSV here." />
      {status && <div className="hint-sm" style={{ marginTop: 10 }}>{status}</div>}
      {rejected.length > 0 && (
        <div className="notice bad" style={{ marginTop: 12 }}>
          <strong>{rejected.length} row{rejected.length === 1 ? '' : 's'} skipped:</strong>
          <ul className="problem-list">{rejected.map((r) => <li key={r.row}>Row {r.row}: {r.reason}</li>)}</ul>
        </div>
      )}
    </div>
  );
}

/* What this round has deleted, and the way back.
 *
 * There was no undo of any kind before this. The button that needed it most was CSV "Replace
 * Everything", which removed every headline in the round with no confirm — and because a CSV
 * cannot carry images, it destroyed every fake page's artwork along with them.
 *
 * Restoring brings the row back exactly as it was, artwork included, because nothing was ever
 * destroyed: the slug is still reserved and the images are still attached to the record.
 */
function TrashPanel({ roundId, onRestored }: { roundId: string; onRestored: () => void }) {
  const [rows, setRows] = useState<RoundItem[] | null>(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.trash(roundId)
      .then((r) => { setRows(r.items); setDays(r.keptForDays || 30); })
      .catch((err) => { setRows([]); setError(err instanceof Error ? err.message : 'Could not read the trash.'); });
  }, [roundId]);
  useEffect(load, [load]);

  async function restore(item: RoundItem) {
    setBusy(item.id);
    setError(null);
    try {
      await api.restoreItem(item.id);
      setRows((cur) => (cur ? cur.filter((r) => r.id !== item.id) : cur));
      // The list behind the panel is now wrong by one — reload it rather than patching it.
      onRestored();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not restore that headline.');
    } finally {
      setBusy(null);
    }
  }

  /* Days remaining, not the date it was deleted. "Gone on the 13th" needs arithmetic; "6 days
     left" is the thing someone actually needs to know before closing the panel. */
  const daysLeft = (deletedAt: string) => {
    const at = Date.parse(String(deletedAt || '').replace(' ', 'T') + 'Z');
    if (!Number.isFinite(at)) return null;
    return Math.max(0, days - Math.floor((Date.now() - at) / 86400000));
  };

  return (
    <div className="panel">
      <h2>Recently Deleted</h2>
      <p>Deleted headlines are kept for {days} days, artwork and all. After that they are gone for good.</p>
      {error && <div className="notice bad">{error}</div>}
      {rows === null ? (
        <div className="hint-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hint-sm">Nothing deleted from this list.</div>
      ) : (
        <div className="activity-list">
          {rows.map((r) => {
            const left = daysLeft(r.deletedAt || '');
            return (
              <div className="activity-row trash-row" key={r.id}>
                <span className="activity-who">{r.verdict === 'fake' ? 'Fake' : 'Real'}</span>
                <span className="activity-what">
                  {r.headline || <em>Untitled headline</em>}
                  {r.verdict === 'fake' && r.images.length > 0 && ' · has artwork'}
                </span>
                <span className="activity-when">
                  {left === null ? '' : left === 0 ? 'Goes today' : `${left} day${left === 1 ? '' : 's'} left`}
                </span>
                <button className="btn ghost" disabled={busy === r.id} onClick={() => void restore(r)}>
                  {busy === r.id ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Every time this round was played.
 *
 * Until now the app recorded that a share LINK was fetched — 26 views on Series 1 and not one
 * word about what happened in any of them. This is the other half: how far he got, what the
 * tally was, and whether it was a real shoot or somebody checking their own work. */
function SessionsPanel({ roundId }: { roundId: string }) {
  const [rows, setRows] = useState<PlaySession[] | null>(null);
  useEffect(() => { void api.sessions(roundId).then(setRows).catch(() => setRows([])); }, [roundId]);

  const when = (iso: string) => (iso ? new Date(iso.replace(' ', 'T')).toLocaleString() : '');
  // Naming the source is the point: a preview is not a recording, and the panel must not imply it is.
  const label: Record<string, string> = {
    share: 'Play link', modes: "Adorian's screen", preview: 'Team preview',
  };

  return (
    <div className="panel">
      <h2>Plays</h2>
      <p>
        Every time this was opened and started, how far it got, and where from. A team preview is
        listed too, and never counts as a recording.
      </p>
      {rows === null ? (
        <div className="hint-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hint-sm">Not played yet. A row appears here the moment someone presses START.</div>
      ) : (
        <div className="activity-list">
          {rows.map((r) => (
            <div className="activity-row" key={r.id}>
              <span className="activity-who">{label[r.source] || r.source || 'Played'}</span>
              <span className="activity-what">
                {r.finished ? 'Finished' : `Stopped at ${r.reached + 1}`}
                {' · '}{r.revealed} of {r.total || '?'} revealed
                {r.revealed > 0 && <> · <span className="c-real">{r.realCount} real</span> · <span className="c-fake">{r.fakeCount} fake</span></>}
              </span>
              <span className="activity-when">{when(r.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityPanel({ roundId }: { roundId: string }) {
  const [rows, setRows] = useState<ActivityEntry[] | null>(null);
  useEffect(() => { void api.activity(roundId).then(setRows).catch(() => setRows([])); }, [roundId]);

  return (
    <div className="panel">
      <h2>History</h2>
      <p>Every change to this series, with who made it.</p>
      {rows === null ? (
        <div className="hint-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="hint-sm">Nothing recorded yet.</div>
      ) : (
        <div className="activity-list">
          {rows.map((r) => (
            <div className="activity-row" key={r.id}>
              <span className="activity-who">{r.who}</span>
              <span className="activity-what">{r.notes || r.type}</span>
              <span className="activity-when">{r.at ? new Date(r.at.replace(' ', 'T')).toLocaleString() : ''}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
